import Vue from 'vue'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import apiClient from '../../apis/apiClient'
import ImageUploadApi from '../../apis/ImageUploadApi'
import { SvgImage } from '../../types/SvgImage'

// TODO: Replace types with the actual types from the API response when available.
export type Icon = {
    attribution: string
    id: string
    license_description: string
    permalink: string
    term: string
    thumbnail_url: string
    collections: {
        id: string
        name: string
        permalink?: string
        creator: {
            name: string
            permalink: string
            username: string
        }
    }[]
    tags: string[]
    styles?: {
        style: string
    }[]
    creator: {
        name: string
        permalink: string
        username: string
    }
    icon_url: string
    our_premium?: boolean
}

type IconResponseDTO = {
    icons: Icon[]
    next_page: string | null
    total: number
}

export type IconRequestDTO = {
    query: string
    next_page?: string | null
}

@Module({
    name: 'IconLibraryModule',
    namespaced: true,
})
export default class IconLibraryModule extends VuexModule {
    private _icons: Icon[] = []
    private _nextPage: string | null | undefined = ''
    private _loading: boolean = false
    private _search: string = ''
    private _totalIcons: number = 0
    private _defaultIconsKey: string = 'e62550f4-f52c-4a3c-a4b1-4e681587d352'

    private _svgImages: SvgImage[] = []
    private _svgImageLoading: string[] = []
    private _settingSvgColor: string[] = []
    private _settingSvgDimensions: string[] = []

    get icons() {
        return this._icons
    }

    get nextPage() {
        return this._nextPage
    }

    get loading() {
        return this._loading
    }

    get search() {
        return this._search
    }

    get totalIcons() {
        return this._totalIcons
    }

    get defaultIconsKey() {
        return this._defaultIconsKey
    }

    get svgImages() {
        return this._svgImages
    }

    get svgImageLoading() {
        return this._svgImageLoading
    }

    get settingSvgColor() {
        return this._settingSvgColor
    }

    @Mutation
    setIcons(icons: Icon[]) {
        this._icons = icons
    }

    @Mutation
    setNextPage(nextPage: string | null) {
        this._nextPage = nextPage
    }

    @Mutation
    setSearch(search: string) {
        this._search = search
    }

    @Mutation
    setTotalIcons(totalIcons: number) {
        this._totalIcons = totalIcons
    }

    @Mutation
    setLoading(loading: boolean) {
        this._loading = loading
    }

    @Mutation
    setSvgImages(payload: { svgImage: SvgImage; initialise?: boolean }) {
        let svgContent: string = payload.svgImage.svgData

        if (payload.initialise) {
            svgContent = svgContent.replace(/^<\?xml[^>]*>\s*<!DOCTYPE[^>]*>/, '')
        }

        const svgData: SvgImage = { id: payload.svgImage.id, svgData: svgContent }
        const svgIndex: number = this._svgImages.findIndex((svgImage: SvgImage) => svgImage.id === payload.svgImage.id)

        if (svgIndex === -1) {
            Vue.set(this._svgImages, this._svgImages.length, svgData)
        }

        if (svgIndex > -1) {
            Vue.set(this._svgImages, svgIndex, svgData)
        }
    }

    @Mutation
    setSvgImageLoading(payload: { id: string; loading: boolean }) {
        if (payload.loading) {
            this._svgImageLoading.push(payload.id)
        } else {
            this._svgImageLoading = this._svgImageLoading.filter((id) => id !== payload.id)
        }
    }

    @Mutation
    setSettingSvgColor(payload: { id: string; isSetting: boolean }) {
        if (payload.isSetting) {
            this._settingSvgColor.push(payload.id)
        } else {
            this._settingSvgColor = this._settingSvgColor.filter((id: string) => id !== payload.id)
        }
    }

    @Mutation
    setSettingSvgDimensions(payload: { id: string; isSetting: boolean }) {
        if (payload.isSetting) {
            this._settingSvgDimensions.push(payload.id)
        } else {
            this._settingSvgDimensions = this._settingSvgDimensions.filter((id: string) => id !== payload.id)
        }
    }

    @Action
    async getSvgImage(id: string) {
        if (this._svgImageLoading.includes(id)) return
        if (this._svgImages.find((svgImage) => svgImage.id === id)) return

        try {
            this.setSvgImageLoading({ id, loading: true })

            const svgData = await ImageUploadApi.getSvg(id)

            this.setSvgImages({ svgImage: { id, svgData }, initialise: true })
        } catch (error) {
            console.error('Error fetching SVG image:', error)
            throw error
        } finally {
            this.setSvgImageLoading({ id, loading: false })
        }
    }

    @Action
    async getIcons(payload: IconRequestDTO) {
        if (!payload.query && !payload.next_page) return

        try {
            this.setLoading(true)

            const response = await apiClient.get('/icon-library', {
                params: payload,
            })

            // Replace the response type here when we have it.
            const data: IconResponseDTO = response.data.data

            // Determines whether we are loading additional icons for the same search.
            const isContinuingSearch = payload.query === this._search && !!this._nextPage

            this.setIcons(isContinuingSearch ? [...this._icons, ...data.icons] : data.icons)
            this.setNextPage(data.next_page === this._nextPage ? null : data.next_page)
            this.setTotalIcons(data.total)
        } catch (error) {
            console.error('Error fetching icons:', error)
            throw error
        } finally {
            this.setLoading(false)
        }
    }

    @Action
    setSvgColor({ id, color }: { id: string; color: string }) {
        if (this._settingSvgColor.includes(id)) return

        this.setSettingSvgColor({ id, isSetting: true })

        const svgImage: SvgImage | undefined = this._svgImages.find((svgImage: SvgImage) => svgImage.id === id)

        if (!svgImage || !svgImage.svgData) {
            this.setSettingSvgColor({ id, isSetting: false })
            return
        }

        let svgData: string = svgImage.svgData

        if (svgData.includes('fill="')) {
            svgData = svgData.replace(/fill="[^"]*"/g, `fill="${color}"`)
            this.setSvgImages({ svgImage: { id, svgData } })
            this.setSettingSvgColor({ id, isSetting: false })
            return
        }

        svgData = svgData.replace(/<svg/g, `<svg fill="${color}"`)

        this.setSvgImages({ svgImage: { id, svgData } })
        this.setSettingSvgColor({ id, isSetting: false })
    }

    @Action
    // TODO: Add type for resizeValues. It's like ImageModel but not exactly the same.
    setSvgDimensions({ id, resizeValues }: { id: string; resizeValues: any }) {
        if (this._settingSvgDimensions.includes(id)) return

        this.setSettingSvgDimensions({ id, isSetting: true })

        const svgImage: SvgImage | undefined = this._svgImages.find((svgImage: SvgImage) => svgImage.id === id)

        if (!svgImage || !svgImage.svgData) {
            this.setSettingSvgDimensions({ id, isSetting: false })
            return
        }

        let svgData: string = svgImage.svgData

        if (!resizeValues.lockAspectRatio && resizeValues.is_icon) {
            svgData = svgData.replace(/<svg/g, `<svg preserveAspectRatio="none"`)
        }

        const width: string = resizeValues.is_inline_image ? `100%` : `${resizeValues.width}px`
        const height: string = resizeValues.is_inline_image ? `100%` : `${resizeValues.height}px`

        const svgMatch: RegExpMatchArray | null = svgData.match(/<svg([^>]*?)>/)

        if (svgMatch && !svgMatch[0].includes('width="')) {
            svgData = svgData.replace(/<svg/g, `<svg width="${width}" height="${height}"`)

            this.setSvgImages({ svgImage: { id, svgData } })
            this.setSettingSvgDimensions({ id, isSetting: false })
            return
        }

        svgData = svgData.replace(/width="[^"]*"/, `width="${width}"`)
        svgData = svgData.replace(/height="[^"]*"/, `height="${height}"`)

        this.setSvgImages({ svgImage: { id, svgData } })
        this.setSettingSvgDimensions({ id, isSetting: false })
    }
}
